import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import CheckboxComp from "themes/elements/Checkbox";
import DatePickerComp from "themes/elements/DatePicker";
import ModalComp from "themes/elements/Modal";
import TimePickerComp from "themes/elements/TimePicker";
import ModalFooter from "../modalFooter";

function ApproveNotification({
  formData,
  setFormData,
  isLoading,
  open,
  setOpen,
  onSubmit,
  expiryDate,
  onCancel,
  title = "Approve this notification?",
}) {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);

  useEffect(() => {
    setDate(null);
    setTime(null);
  }, [open]);

  const validation = () => {
    const errors = [];
    const today = dayjs().startOf("day");
    const currentTime = dayjs().startOf("minute");

    if (formData.schedule_publish) {
      if (!date) {
        errors.scheduleDate = "Schedule date is required";
      } else {
        const scheduleDate = dayjs(date, "DD-MM-YYYY");
        if (scheduleDate.isBefore(today)) {
          errors.scheduleDate = "Schedule date must be in the future";
        } else if (scheduleDate.isSame(today, "day") && time) {
          const scheduleTime = dayjs(time, "HH:mm");
          if (scheduleTime.isBefore(currentTime)) {
            errors.schedule_time = "Schedule time must be in the future";
          }
        }
      }
      if (!time) {
        errors.scheduleTime = "Schedule time is required";
      }
    }

    return errors;
  };

  const checkValidation = () => {
    const errors = validation();
    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((errorMessage) => {
        toast.warning(errorMessage);
      });
    } else {
      onSubmit();
    }
  };

  const disableStartDate = (current) => {
    if (!current) return false;
    const today = dayjs().startOf("day");
    const currentDay = dayjs(current).startOf("day");
    if (currentDay.isBefore(today)) return true; // disable past dates

    if (!expiryDate) return false;
    // Support both DD-MM-YYYY and YYYY-MM-DD from API
    let expiryDay = dayjs(expiryDate, "DD-MM-YYYY", true);
    if (!expiryDay.isValid()) expiryDay = dayjs(expiryDate, "YYYY-MM-DD", true);
    if (!expiryDay.isValid()) return false;
    expiryDay = expiryDay.startOf("day");
    const todayStart = dayjs().startOf("day");
    if (expiryDay.isBefore(todayStart)) return false; // expired notification: allow any future date
    return currentDay.isAfter(expiryDay); // disable dates after expiry
  };

  const handleCheckBox = (value) => {
    if (!value) {
      setFormData((prev) => ({
        ...prev,
        schedule_date: null,
        schedule_time: null,
      }));
      setDate(null);
      setTime(null);
    }
    setFormData((prev) => ({
      ...prev,
      schedule_publish: value,
    }));
  };

  const handleDatePicker = (dateString) => {
    setFormData((prevData) => ({
      ...prevData,
      schedule_date: dateString,
      schedule_time: null,
    }));
    setDate(dateString);
    setTime(null);
  };

  const handleTimePicker = (timeVal, time12HourFormat, time24HourFormat) => {
    setFormData((prevData) => ({
      ...prevData,
      schedule_time: time24HourFormat,
    }));
    setTime(time24HourFormat);
  };

  return (
    <ModalComp
      title={title}
      footer={
        <ModalFooter
          handlePrimaryBtn={() => {
            setOpen(false);
            onCancel?.();
          }}
          primaryBtnvariant="secondary"
          primaryBtnText="Cancel"
          secondaryBtnloading={isLoading}
          handleSecondaryBtn={checkValidation}
          secondaryBtnText="Submit"
        />
      }
      open={open}
      setOpen={setOpen}
    >
      <div className="flex flex-column gap-2">
        <CheckboxComp
          isChecked={formData.schedule_publish}
          onChange={handleCheckBox}
          label="Schedule publishing"
        />
        {formData.schedule_publish && (
          <div className="flex align-center gap-4">
            <DatePickerComp
              value={date}
              setValue={setDate}
              disabled={false}
              disabledDate={disableStartDate}
              onChange={handleDatePicker}
            />
            <TimePickerComp
              date={date}
              onChange={handleTimePicker}
              disabled={!date}
            />
          </div>
        )}
      </div>
    </ModalComp>
  );
}

export default ApproveNotification;
